import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    Validators,
} from '@angular/forms';
import { AppStateFacade } from '@app-state';
import { UiModalComponent } from '@components/ui-modal/ui-modal.component';
import { FormGroupDirective } from '@directives/form-group.directive';
import {
    FeatureFlag,
    IQueueItem,
    IQueueItemComment,
} from '@models';
import {
    QueueItemCommentService,
    QueueItemEventService,
    QueueItemService,
} from '@services';
import UiValidators, { UI_COMMENT_MAX_LENGTH } from '@validators';

import { BehaviorSubject } from 'rxjs';
import {
    finalize,
    map,
    switchMap,
} from 'rxjs/operators';

@Component({
    templateUrl: './transaction-details.modal.html',
    providers: [
        QueueItemService,
        QueueItemCommentService,
        QueueItemEventService,
    ],
    styleUrls: ['./transaction-details.modal.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TransactionDetailsModalComponent
    extends UiModalComponent<IQueueItem>
    implements OnInit, OnDestroy {

    public queueItem = this.context.data;

    public activeTabName = 'Details';
    public formGroup!: FormGroup;

    public loading$ = new BehaviorSubject(false);
    public reload$ = new BehaviorSubject(null);

    @ViewChild('formDirective', { static: true })
    public formDirective?: FormGroupDirective;

    public isQueueSchemaDefinitionsEnabled!: boolean;

    public queueItemComment = this._injector.get<QueueItemCommentService>(QueueItemCommentService);
    public comments$ = this.reload$
        .pipe(
            switchMap(
                () => this.queueItemComment
                    .getByItem(this.queueItem.id)
                    .pipe(
                        map((response: any) => response.data),
                    ),
            ),
        );


    private _appState = this._injector.get<AppStateFacade>(AppStateFacade);

    ngOnInit() {
        this.formGroup = this._injector.get<FormBuilder>(FormBuilder).group({
            'text': [
                '',
                Validators.compose([
                    UiValidators.required(),
                    Validators.maxLength(UI_COMMENT_MAX_LENGTH),
                ]),
            ],
        });
        this.isQueueSchemaDefinitionsEnabled = this._appState.settings.isFeatureEnabled(FeatureFlag.QueueSchemaDefinitions);
    }

    ngOnDestroy() {
        this.reload$.complete();
        this.loading$.complete();
    }

    public onSubmitComment(ev?: Event) {
        if (ev) {
            ev.preventDefault();
            ev.stopImmediatePropagation();
        }

        this.loading$.next(true);

        const payload: Partial<IQueueItemComment> = {
            queueItemId: this.queueItem.id,
            text: this.formGroup.get('text')!.value.trim(),
        };

        this.queueItemComment
            .add(payload as IQueueItemComment)
            .pipe(
                finalize(
                    () => this.loading$.next(false),
                ),
            )
            .subscribe(
                () => {
                    this.reload$.next(null);
                    this.formDirective!.resetForm();
                },
            );
    }
}
